import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import {
  ShieldCheck, // For Security/RBAC
  Lock,        // For Encryption
  History,     // For Data History
  Bell,        // For Alerts
  Brain,       // For Predictive Analytics (PsychologyIcon was a bit abstract)
  TerminalSquare, // For Device Provisioning/Code
  Cloud,       // For Scalable Infrastructure
  Blocks       // For API/Integrations (ApiIcon was a bit generic)
} from 'lucide-react';

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: "easeOut" }
  }
};

const staggerChildren = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

export default function PlatformFeatures({ viewport, isRTL }) {
  const { t } = useTranslation();
  const { isMobile = false } = viewport || {};

  const featuresData = [
    {
      key: 'rbac',
      icon: <ShieldCheck size={24} />, // Adjusted icon size for consistency
      color: 'hsl(var(--green-500))' // Use HSL from theme variables
    },
    {
      key: 'encryption',
      icon: <Lock size={24} />,
      color: 'hsl(var(--sky-500))'
    },
    {
      key: 'history',
      icon: <History size={24} />,
      color: 'hsl(var(--yellow-500))' 
    },
    {
      key: 'alerts',
      icon: <Bell size={24} />,
      color: 'hsl(var(--red-500))'
    },
    {
      key: 'analytics',
      icon: <Brain size={24} />,
      color: 'hsl(var(--blue-500))'
    },
    {
      key: 'provisioning',
      icon: <TerminalSquare size={24} />,
      color: 'hsl(var(--violet-500))' 
    },
    {
      key: 'infrastructure',
      icon: <Cloud size={24} />,
      color: 'hsl(var(--cyan-500))' 
    },
    {
      key: 'api',
      icon: <Blocks size={24} />,
      color: 'hsl(var(--emerald-600))' 
    }
  ];

  const features = featuresData.map(feature => ({
    ...feature,
    title: t(`homepage.features.${feature.key}.title`),
    description: t(`homepage.features.${feature.key}.description`),
  }));

  return (
    <section // Changed div to section for semantics
      id="platform-features"
      className="relative py-20 md:py-28 lg:py-32 bg-muted/30" // Use muted from theme, adjust padding
    >
      {/* Optional: Removed complex SVG background for minimality */}
      {/* <div className="absolute inset-0 opacity-[0.02] dark:opacity-[0.03]" style={{backgroundImage: 'url(...)'}}></div> */}

      <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }} // Adjusted amount
          variants={staggerChildren}
          className="text-center mb-12 md:mb-20 max-w-2xl mx-auto" // Consistent with SegmentOverview
        >
          <motion.div variants={fadeInUp}>
            <Badge
              variant="outline"
              className="mb-3 px-3 py-1 text-xs font-medium rounded-full border-primary/30 bg-primary/10 text-primary"
            >
              {t('homepage.features.badge', 'Powerful & Reliable')}
            </Badge>
          </motion.div>
          <motion.div variants={fadeInUp}>
            <h2 className="text-3xl md:text-4xl font-bold mb-4 tracking-tight text-foreground">
              {t('homepage.features.title', 'Advanced Features for Total Control')}
            </h2>
          </motion.div>
          <motion.div variants={fadeInUp}>
            <p className="text-md md:text-lg text-muted-foreground max-w-xl mx-auto">
              {t('homepage.features.subtitle', 'Our platform is packed with features designed for security, scalability, and ease of use, giving you complete command over your operations.')}
            </p>
          </motion.div>
        </motion.div>

        <motion.div
          variants={staggerChildren}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: isMobile ? 0.05 : 0.1 }} // Adjust viewport amount for mobile
        >
          <div className={cn(
            "grid gap-x-6 gap-y-8", // Adjusted gap
            "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" // Retain responsive columns
          )}>
            {features.map((feature) => (
              <motion.div
                key={feature.key}
                variants={fadeInUp}
                className="flex" // Keep for flex consistency if items have different heights
              >
                <div
                  className={cn(
                    "w-full bg-background rounded-lg p-5", // Use background, adjusted padding
                    "border border-border/50", // Subtle border
                    "transition-all duration-300 ease-in-out",
                    "hover:shadow-lg hover:border-primary/30 hover:-translate-y-1", // Minimal hover
                    "flex items-start gap-4 group" // Group for icon hover effects
                  )}
                  // Consider removing inline style for borderLeft if not a strong design choice for minimality
                  // style={{ borderLeftWidth: '3px', borderLeftColor: feature.color }} 
                >
                  {/* Icon Container */}
                  <div
                    className="flex-shrink-0 w-10 h-10 rounded-md flex items-center justify-center transition-colors duration-300 group-hover:bg-primary/10"
                    style={{
                      backgroundColor: `color-mix(in srgb, ${feature.color} 10%, transparent)`, // Softer background
                      color: feature.color
                    }}
                  >
                    {feature.icon}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-foreground text-base leading-tight mb-1">
                      {feature.title}
                    </h3>
                    <p className="text-muted-foreground text-sm leading-normal line-clamp-2"> {/* Adjusted line-clamp for brevity */}
                      {feature.description}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}

